import React, { useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { 
  Building, 
  DollarSign, 
  Users, 
  Calendar,
  FileText,
  MessageCircle,
  TrendingUp,
  Star,
  Settings,
  BarChart3,
  Receipt,
  BookOpen,
  Mail,
  Edit,
  Video,
  ChevronDown,
  ChevronRight,
  Plus,
  Search,
  Clock,
  Smartphone
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { usePlatform } from '../../contexts/PlatformContext';
import { useAuth } from '../../contexts/AuthContext';
import PlatformToggle from '../layout/PlatformToggle';
import PostRequirement from './organization/PostRequirement';
import ProposalsReceived from './organization/ProposalsReceived';
import MyTrainers from './organization/MyTrainers';
import PurchaseOrders from './organization/PurchaseOrders';
import OrganizationCommunication from './organization/OrganizationCommunication';
import TrainingSchedule from './organization/TrainingSchedule';
import OrganizationDashboard from './organization/OrganizationDashboard';
import AdvancedTrainerSearch from './organization/AdvancedTrainerSearch';
import DigiTPro from '../../pages/DigiTPro';
import ProposalDetails from '../../pages/ProposalDetails';
import MessageCompose from '../../pages/MessageCompose';
import TrainingFeedback from './TrainingFeedback';

const OrganizationBusinessHub = () => {
  const { toggleMode } = usePlatform();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active section from current route
  const getActiveSectionFromPath = () => {
    const path = location.pathname;
    
    if (path === '/business' || path === '/business/') return 'dashboard';
    if (path.startsWith('/business/post-requirement')) return 'post-requirement';
    if (path.startsWith('/business/proposals-received')) return 'proposals-received';
    if (path.startsWith('/business/advanced-search')) return 'advanced-search';
    if (path.startsWith('/business/my-trainers')) return 'my-trainers';
    if (path.startsWith('/business/purchase-orders')) return 'purchase-orders';
    if (path.startsWith('/business/communication')) return 'communication';
    if (path.startsWith('/business/training-schedule')) return 'training-schedule';
    
    return 'dashboard';
  };

  const activeSection = getActiveSectionFromPath();

  // Mock organization data
  const orgData = {
    totalSpent: 125000,
    activeTrainers: 8,
    upcomingTrainings: 3,
    notifications: {
      proposals: 5,
      messages: 3,
      trainings: 2,
      orders: 1
    }
  };

  // Calculate total notifications for the toggle badge
  const businessNotifications = Object.values(orgData.notifications).reduce((sum, count) => sum + count, 0);

  const navigationItems = [
    { 
      id: 'dashboard',
      path: '/business',
      label: 'Dashboard', 
      icon: BarChart3, 
      badge: null 
    },
    { 
      id: 'post-requirement',
      path: '/business/post-requirement',
      label: 'Post a Requirement', 
      icon: Plus, 
      badge: null 
    },
    { 
      id: 'proposals-received',
      path: '/business/proposals-received',
      label: 'Proposals Received', 
      icon: FileText, 
      badge: orgData.notifications.proposals 
    },
    { 
      id: 'advanced-search',
      path: '/business/advanced-search',
      label: 'Advanced Search', 
      icon: Search, 
      badge: null 
    },
    { 
      id: 'my-trainers',
      path: '/business/my-trainers',
      label: 'My Trainers', 
      icon: Users, 
      badge: null 
    },
    { 
      id: 'purchase-orders',
      path: '/business/purchase-orders',
      label: 'Purchase Orders', 
      icon: Receipt, 
      badge: orgData.notifications.orders 
    },
    { 
      id: 'communication',
      path: '/business/communication',
      label: 'Communication', 
      icon: MessageCircle, 
      badge: orgData.notifications.messages 
    },
    { 
      id: 'training-schedule',
      path: '/business/training-schedule',
      label: 'Training Schedule', 
      icon: Calendar, 
      badge: orgData.notifications.trainings 
    },
    { 
      id: 'training-feedback',
      path: '/business/training-feedback',
      label: 'Training Feedback', 
      icon: Smartphone, 
      badge: null 
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Organization Business Hub Header */}
      <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-blue-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-4">
                <Avatar className="w-16 h-16 border-4 border-white shadow-lg">
                  <AvatarImage src={user?.avatar} alt={user?.organizationName || user?.name} />
                  <AvatarFallback className="bg-blue-600 text-white text-xl font-bold">
                    <Building className="w-8 h-8" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h1 className="text-2xl font-bold">Organization Hub</h1>
                  <div className="flex items-center space-x-4 mt-1">
                    <p className="text-blue-100 font-medium">{user?.organizationName || 'TechCorp Solutions'}</p>
                    <p className="text-blue-200 text-sm">Training Management Dashboard</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-8">
                <div className="text-center">
                  <div className="flex items-center text-2xl font-bold">
                    <DollarSign className="w-6 h-6 mr-1" />
                    ${orgData.totalSpent.toLocaleString()}
                  </div>
                  <p className="text-blue-200 text-sm">Total spent</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center text-2xl font-bold">
                    <Users className="w-6 h-6 mr-1" />
                    {orgData.activeTrainers}
                  </div>
                  <p className="text-blue-200 text-sm">Active trainers</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center text-2xl font-bold">
                    <Calendar className="w-6 h-6 mr-1" />
                    {orgData.upcomingTrainings}
                  </div>
                  <p className="text-blue-200 text-sm">Upcoming</p>
                </div>
              </div>
            </div>
            
            {/* Platform Toggle for Organizations */}
            <div className="flex items-center space-x-4">
              <PlatformToggle 
                currentMode="business"
                onToggle={toggleMode}
                businessNotifications={businessNotifications}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Organization Hub Layout */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Organization Navigation Sidebar */}
          <div className="col-span-3">
            <Card className="sticky top-6 shadow-soft">
              <CardContent className="p-4">
                <div className="space-y-1 mb-6">
                  {navigationItems.map((item) => {
                    const Icon = item.icon;
                    
                    return (
                      <div
                        key={item.id}
                        onClick={() => navigate(item.path)}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-sm ${
                          activeSection === item.id
                            ? 'bg-blue-50 text-blue-800 border-l-4 border-blue-600 shadow-sm'
                            : 'hover:bg-gray-50 text-gray-700 hover:text-blue-700'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon className="w-5 h-5" />
                          <span className="font-medium text-sm">{item.label}</span>
                        </div>
                        {item.badge && (
                          <Badge variant="destructive" className="bg-red-500 text-white text-xs px-2">
                            {item.badge}
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Quick Actions */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-gray-800 mb-3 text-xs uppercase tracking-wide px-3">
                    Quick Actions
                  </h3>
                  <div className="space-y-2">
                    <Button 
                      size="sm" 
                      className="w-full justify-start bg-blue-600 hover:bg-blue-700"
                      onClick={() => navigate('/business/post-requirement')}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Post New Requirement
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => navigate('/business/advanced-search')}
                    >
                      <Search className="w-4 h-4 mr-2" />
                      Advanced Search
                    </Button>
                  </div>
                </div>

                {/* Organization Settings */}
                <div className="border-t pt-4 mt-4">
                  <div
                    onClick={() => navigate('/business/settings')}
                    className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                      activeSection === 'settings'
                        ? 'bg-blue-50 text-blue-800 border-l-4 border-blue-600'
                        : 'hover:bg-gray-50 text-gray-700 hover:text-blue-700'
                    }`}
                  >
                    <Settings className="w-5 h-5" />
                    <span className="font-medium text-sm">Organization Settings</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Organization Content Area */}
          <div className="col-span-9">
            <Routes>
              {/* Default/Dashboard Route */}
              <Route index element={<Navigate to="/business" replace />} />
              <Route path="/" element={<OrganizationDashboard orgData={orgData} />} />
              <Route path="/business" element={<OrganizationDashboard orgData={orgData} />} />
              
              {/* Organization Business Hub Routes */}
              <Route path="/business/post-requirement" element={<PostRequirement />} />
              <Route path="/business/proposals-received" element={<ProposalsReceived />} />
              <Route path="/business/advanced-search" element={<AdvancedTrainerSearch />} />
              <Route path="/business/my-trainers" element={<MyTrainers />} />
              <Route path="/business/purchase-orders" element={<PurchaseOrders />} />
              <Route path="/business/communication" element={<OrganizationCommunication />} />
              <Route path="/business/training-schedule" element={<TrainingSchedule />} />
              
              {/* Training Feedback Route */}
              <Route path="/business/training-feedback" element={<TrainingFeedback />} />
              
              {/* Shared Routes (work in both social and business) */}
              <Route path="/digitpro" element={<DigiTPro />} />
              <Route path="/digitpro/:trainerId" element={<DigiTPro />} />
              <Route path="/proposal/:proposalId" element={<ProposalDetails />} />
              <Route path="/messages/compose" element={<MessageCompose />} />
              
              {/* Fallback to Dashboard */}
              <Route path="*" element={<Navigate to="/business" replace />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationBusinessHub;